if (!customElements.get('localization-form')) {
  customElements.define(
    'localization-form',
    class LocalizationForm extends HTMLElement {
      constructor() {
        super();
        this.mql = window.matchMedia('(min-width: 750px)');
        this.header = document.querySelector('.header-wrapper');
        this.elements = {
          input: this.querySelector('input[name="locale_code"], input[name="country_code"]'),
          button: this.querySelector('button.localization-form__select, button.disclosure__button, button.footer-currency-btn'),
          panel: this.querySelector('.disclosure__list-wrapper'),
          search: this.querySelector('input[name="country_filter"]'),
          closeButton: this.querySelector('.country-selector__close-button'),
          resetButton: this.querySelector('.country-filter__reset-button'),
          searchIcon: this.querySelector('.country-filter__search-icon'),
          liveRegion: this.querySelector('[id$="-sr-country-search-results"], #sr-country-search-results'),
          overlay: this.querySelector('.country-selector__overlay'),
        };

        if (!this.elements.button || !this.elements.panel) return;

        this.boundOnDocumentClick = this.onDocumentClick.bind(this);
        this.addEventListener('keyup', this.onContainerKeyUp.bind(this));
        this.addEventListener('keydown', this.onContainerKeyDown.bind(this));
        this.addEventListener('focusout', this.closeSelector.bind(this));
        this.elements.button.addEventListener('click', this.openSelector.bind(this));

        if (this.elements.search) {
          this.elements.search.addEventListener('keyup', this.filterCountries.bind(this));
          this.elements.search.addEventListener('focus', this.onSearchFocus.bind(this));
          this.elements.search.addEventListener('blur', this.onSearchBlur.bind(this));
          this.elements.search.addEventListener('keydown', this.onSearchKeyDown.bind(this));
        }
        if (this.elements.closeButton) {
          this.elements.closeButton.addEventListener('click', this.hidePanel.bind(this));
        }
        if (this.elements.resetButton) {
          this.elements.resetButton.addEventListener('click', this.resetFilter.bind(this));
          this.elements.resetButton.addEventListener('mousedown', (event) => event.preventDefault());
        }
        if (this.elements.overlay) {
          this.elements.overlay.addEventListener('click', this.hidePanel.bind(this));
        }

        this.querySelectorAll('a').forEach((item) => item.addEventListener('click', this.onItemClick.bind(this)));

        // Panel event delegation: guarantees option clicks and selections submit reliably
        this.elements.panel.addEventListener('click', (event) => {
          const item = event.target.closest('a');
          if (item) {
            this.onItemClick(event, item);
          }
        });
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.boundOnDocumentClick);
      }

      hidePanel() {
        if (!this.elements.button || !this.elements.panel) return;
        this.elements.button.setAttribute('aria-expanded', 'false');
        this.elements.panel.setAttribute('hidden', true);
        document.removeEventListener('click', this.boundOnDocumentClick);

        if (this.elements.search) {
          this.elements.search.value = '';
          this.filterCountries();
          this.elements.search.setAttribute('aria-activedescendant', '');
        }
        document.body.classList.remove('overflow-hidden-mobile');
        if (this.closest('.menu-drawer')) {
          document.querySelector('.menu-drawer')?.classList.remove('country-selector-open');
        }
        if (this.header) {
          this.header.preventHide = false;
        }
      }

      onContainerKeyDown(event) {
        const focusableItems = Array.from(this.querySelectorAll('a')).filter(
          (item) => !item.parentElement.classList.contains('hidden')
        );
        let focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
        let itemToFocus;

        switch (event.code.toUpperCase()) {
          case 'ARROWUP':
            event.preventDefault();
            itemToFocus =
              focusedItemIndex > 0 ? focusableItems[focusedItemIndex - 1] : focusableItems[focusableItems.length - 1];
            itemToFocus?.focus();
            break;
          case 'ARROWDOWN':
            event.preventDefault();
            itemToFocus =
              focusedItemIndex < focusableItems.length - 1 ? focusableItems[focusedItemIndex + 1] : focusableItems[0];
            itemToFocus?.focus();
            break;
        }

        if (!this.elements.search) return;

        setTimeout(() => {
          focusedItemIndex = focusableItems.findIndex((item) => item === document.activeElement);
          if (focusedItemIndex > -1) {
            this.elements.search.setAttribute('aria-activedescendant', focusableItems[focusedItemIndex].id);
          } else {
            this.elements.search.setAttribute('aria-activedescendant', '');
          }
        });
      }

      onContainerKeyUp(event) {
        event.preventDefault();

        switch (event.code.toUpperCase()) {
          case 'ESCAPE':
            if (this.elements.button.getAttribute('aria-expanded') === 'false') return;
            this.hidePanel();
            event.stopPropagation();
            this.elements.button.focus();
            break;
          case 'SPACE':
            if (this.elements.button.getAttribute('aria-expanded') === 'true') return;
            this.openSelector();
            break;
        }
      }

      onItemClick(event, element) {
        event.preventDefault();
        const item = element || event.currentTarget || event.target.closest('a');
        if (!item) return;

        const value = item.dataset.value;
        if (!value) return;

        const form = this.querySelector('form');
        const input =
          this.querySelector('input[name="locale_code"], input[name="country_code"]') || this.elements.input;

        if (input) {
          input.value = value;
        }
        if (form) {
          form.submit();
        }
      }

      openSelector() {
        const isExpanded = this.elements.button.getAttribute('aria-expanded') === 'true';
        if (isExpanded) {
          this.hidePanel();
          return;
        }

        this.elements.button.focus();
        this.elements.panel.removeAttribute('hidden');
        this.elements.button.setAttribute('aria-expanded', 'true');

        if (!document.body.classList.contains('overflow-hidden-tablet')) {
          document.body.classList.add('overflow-hidden-mobile');
        }
        if (this.elements.search && this.mql.matches) {
          this.elements.search.focus();
        }
        if (this.header && this.hasAttribute('data-prevent-hide')) {
          this.header.preventHide = true;
        }
        if (this.closest('.menu-drawer')) {
          document.querySelector('.menu-drawer')?.classList.add('country-selector-open');
        }

        setTimeout(() => {
          document.addEventListener('click', this.boundOnDocumentClick);
        }, 0);
      }

      onDocumentClick(event) {
        if (!this.contains(event.target)) {
          this.hidePanel();
        }
      }

      closeSelector(event) {
        if (event.target.classList.contains('country-selector__overlay')) {
          this.hidePanel();
          return;
        }
        // Only close on focusout if focus moved to an element outside this component
        if (event.relatedTarget && !this.contains(event.relatedTarget)) {
          this.hidePanel();
        }
      }

      normalizeString(str) {
        return str
          .normalize('NFD')
          .replace(/\p{Diacritic}/gu, '')
          .toLowerCase();
      }

      filterCountries() {
        const searchValue = this.normalizeString(this.elements.search.value);
        const popularCountries = this.querySelector('.popular-countries');
        const allCountries = this.querySelectorAll('a');
        let visibleCountries = allCountries.length;

        if (this.elements.resetButton) {
          this.elements.resetButton.classList.toggle('hidden', !searchValue);
        }

        if (popularCountries) {
          popularCountries.classList.toggle('hidden', searchValue);
        }

        allCountries.forEach((item) => {
          const countrySpan = item.querySelector('.country');
          const countryName = countrySpan ? this.normalizeString(countrySpan.textContent) : '';
          if (countryName.indexOf(searchValue) > -1) {
            item.parentElement.classList.remove('hidden');
            visibleCountries++;
          } else {
            item.parentElement.classList.add('hidden');
            visibleCountries--;
          }
        });

        if (this.elements.liveRegion && window.accessibilityStrings?.countrySelectorSearchCount) {
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.countrySelectorSearchCount.replace(
            '[count]',
            visibleCountries
          );
        }

        const countrySelector = this.querySelector('.country-selector');
        if (countrySelector) countrySelector.scrollTop = 0;
        const countryList = this.querySelector('.country-selector__list');
        if (countryList) countryList.scrollTop = 0;
      }

      resetFilter(event) {
        event.stopPropagation();
        if (this.elements.search) {
          this.elements.search.value = '';
          this.filterCountries();
          this.elements.search.focus();
        }
      }

      onSearchFocus() {
        if (this.elements.searchIcon) {
          this.elements.searchIcon.classList.add('country-filter__search-icon--hidden');
        }
      }

      onSearchBlur() {
        if (!this.elements.search?.value && this.elements.searchIcon) {
          this.elements.searchIcon.classList.remove('country-filter__search-icon--hidden');
        }
      }

      onSearchKeyDown(event) {
        if (event.code.toUpperCase() === 'ENTER') {
          event.preventDefault();
        }
      }
    }
  );
}
